import { useMemo } from 'react'
import { useGetList, useGetOne } from 'react-admin'

// Shared stable empty arrays: a `= []` destructuring default allocates a fresh
// array on every render where the query data is undefined (e.g. a disabled
// shared-devices query), which would churn enhancedDevices' identity and defeat
// the memoization below.
const EMPTY: any[] = []

interface EnhancedDevice {
  id: string | number
  name: string
  pet: any
  start_time?: number
  timezone?: number
  transfer_date?: string
}

/**
 * Custom hook that fetches devices for a pet, including shared devices based on
 * device history. Enables transparent device sharing between pets that have
 * shared device history (e.g. an owner who switched phones).
 *
 * Built on react-admin's react-query-backed useGetList/useGetOne rather than a
 * manual useState/useEffect fetch. The hand-rolled version churned its output
 * array identity on every re-render/refetch, which cascaded into a perpetual
 * metrics-query refetch in useDeviceMetrics — leaving the virtual "All Devices"
 * CSV/PDF export stuck loading with zero metrics. react-query gives stable,
 * deduplicated, cached results shared across every consumer.
 */
export function usePetDevicesWithHistory(petId: string | number | null) {
  const enabled = petId != null && petId !== ''

  // Current devices for the pet
  const {
    data: currentDevices = EMPTY,
    isLoading: devicesLoading,
    error: devicesError,
  } = useGetList(
    'devices',
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: 'id', order: 'ASC' },
      filter: { pet: petId },
    },
    { enabled },
  )

  // Pet record — needed to discover any historical ("previous_device") sensor
  const { data: petData, error: petError } = useGetOne('pets', { id: petId as any }, { enabled })

  // Resolve the previous device name from the pet profile (object or JSON string)
  const previousDeviceName = useMemo(() => {
    const profile = (petData as any)?.profile
    if (!profile) return null
    try {
      const profileObj = typeof profile === 'string' ? JSON.parse(profile) : profile
      return typeof profileObj?.previous_device === 'string' ? profileObj.previous_device : null
    } catch {
      return null
    }
  }, [petData])

  // Shared/historical devices matched by name (only fetched when one exists)
  const { data: sharedDevicesRaw = EMPTY, error: sharedError } = useGetList(
    'devices',
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: 'id', order: 'ASC' },
      filter: { name: previousDeviceName },
    },
    { enabled: enabled && !!previousDeviceName },
  )

  const enhancedDevices = useMemo<EnhancedDevice[]>(() => {
    const currentIds = new Set(currentDevices.map((d) => d.id))
    // Only add shared devices that aren't already in current devices. No
    // isShared flag — historical devices look identical to owned ones.
    const shared = sharedDevicesRaw.filter((d) => !currentIds.has(d.id))
    return [...currentDevices, ...shared] as EnhancedDevice[]
  }, [currentDevices, sharedDevicesRaw])

  return {
    data: enhancedDevices,
    // Loading follows the current devices only: folding in the two follow-up
    // queries would flip a settled gate back to loading mid-page (the PetShow
    // flicker class). An ERROR from any of the three must surface, though —
    // a failed pet record or previous_device lookup leaves the list without
    // the earlier sensor, and consumers (useDeviceMetrics → the exports' gate)
    // would otherwise treat that short list as complete (eng-brain pass 76).
    isLoading: devicesLoading,
    error: devicesError ?? petError ?? sharedError ?? null,
    hasCurrentDevices: currentDevices.length > 0,
    hasHistoricalDevices: sharedDevicesRaw.length > 0,
  }
}
